import { CommonModule, isPlatformServer } from '@angular/common';
import {
	Component,
	computed,
	effect,
	inject,
	input,
	PLATFORM_ID,
	signal,
	untracked,
	ViewEncapsulation,
} from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';
import type { InputsType } from 'ng-dynamic-component';
import { DynamicModule } from 'ng-dynamic-component';

import type { FiltersInterface } from '@valk-nx/compositions/ui-filters/src/lib/contracts/filters.interface';
import { SUPPORTED_FILTER_TYPES } from '@valk-nx/compositions/ui-filters/src/lib/contracts/filters.interface';
import {
	DEFAULT_SORT_OPTIONS,
	FilterHeadingComponent,
} from '@valk-nx/compositions/ui-filters/src/lib/filter-heading/filter-heading';
import { FiltersComponent } from '@valk-nx/compositions/ui-filters/src/lib/filters.component';
import { negativeHeader$ } from '@valk-nx/core/lib/core';
import type { PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import { injectQueryParamsMap } from '@valk-nx/services/query-params';
import { CarouselComponent as SbCarouselComponent } from '@valk-nx/storyblok/components/carousel/src/lib/carousel';
import { CarouselWithAvailabilityWidgetComponent as SbCarouselWithAvailabilityWidgetComponent } from '@valk-nx/storyblok/components/carousel-with-availability-widget/src/lib/carousel-with-availability-widget';
import { HeadingGroupComponent } from '@valk-nx/storyblok/components/heading-group/src/lib/heading-group';
import { StoryblokItemsSliderComponent } from '@valk-nx/storyblok/components/items-slider-section/src/lib/items-slider-section';
import { NewsletterComponent as SbNewsletterComponent } from '@valk-nx/storyblok/components/newsletter/src/lib/newsletter';
import { ReferenceComponent } from '@valk-nx/storyblok/components/reference/src/lib/reference.component';
import { RichTextComponent } from '@valk-nx/storyblok/components/richtext/src/lib/rich-text';
import { SectionComponent } from '@valk-nx/storyblok/components/section/src/lib/section';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { extractCarouselImages } from '@valk-nx/storyblok-helpers/src/lib/general/carousel-images.helper';
import {
	FilterableListService,
	preselectedFilters,
} from '@valk-nx/storyblok-services/src/lib/services/filterable-list.service';
import { MetadataService } from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import type {
	FilterableHotel,
	FilterableListFilter,
	FilterableListItems,
	FilterableListScenario,
	FilterablePackageBatch,
} from '@valk-nx/storyblok-types/src/lib/types/filterable-list.service.interface';
import type {
	HeadingGroupSection,
	ISbStoryBlok,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

import { VdcHotels } from './lists/vdc-hotels/vdc-hotels';
import { VdcPackages } from './lists/vdc-packages/vdc-packages';
import { VdcvhiHotels } from './lists/vdcvhi-hotels/vdcvhi-hotels';
import { toBaseUrl } from './filterable-list.helpers';
import type {
	FilterableListHotel,
	FilterableListType,
} from './filterable-list.interface';

const carouselComponents = {
	carousel: SbCarouselComponent,
	carouselWithAvailabilityWidget: SbCarouselWithAvailabilityWidgetComponent,
};

const titleComponents = {
	headingGroup: HeadingGroupComponent,
};

const richTextComponents = {
	richText: RichTextComponent,
};

const subContentComponents = {
	itemsSliderSection: StoryblokItemsSliderComponent,
	newsletter: SbNewsletterComponent,
	reference: ReferenceComponent,
	section: SectionComponent,
};

const scenarioMap = {
	vdc: {
		hotels: 'vdc-hotels',
		packages: 'vdc-packages',
		events: 'vdc-events',
		rooms: 'vdc-rooms',
	},
	vdcvhi: {
		hotels: 'vdcvhi-hotels',
		packages: 'vdcvhi-packages',
		events: 'vdcvhi-events',
		rooms: 'vdcvhi-rooms',
	},
} as const satisfies Record<
	FilterableListHotel,
	Record<FilterableListType, FilterableListScenario>
>;

@Component({
	encapsulation: ViewEncapsulation.None,
	selector: 'sb-filterable-list',
	templateUrl: './filterable-list.html',
	imports: [
		CommonModule,
		DynamicModule,
		FiltersComponent,
		FilterHeadingComponent,
		TranslatePipe,
		VdcHotels,
		VdcPackages,
		VdcvhiHotels,
	],
})
export class FilterableList extends StoryblokRootDirective {
	protected readonly filterableListService = inject(FilterableListService);
	private readonly urlFilterParams = injectQueryParamsMap();
	private readonly metadataService = inject(MetadataService);
	private readonly platformId = inject(PLATFORM_ID);

	hotel = input.required<FilterableListHotel>();
	type = input.required<FilterableListType>();
	carousel = input.required<ISbStoryBlok[]>();
	title = input.required<HeadingGroupSection[]>();
	content = input.required<ISbStoryBlok[]>();
	subContent = input<ISbStoryBlok[]>();
	listItems = input<FilterableListItems>({});
	showPrices = input<boolean>(true);
	baseUrl = input('', { transform: toBaseUrl });

	sortOption = signal<string>('name-asc');

	activeFilterCount = computed(() =>
		Object.values(this.filterableListService.filters()).reduce(
			(sum, values) => sum + values.length,
			0,
		),
	);

	valkBusiness = computed<boolean>(() => !!this.listItems()?.valkBusiness);

	activeScenario = computed<FilterableListScenario | null>(() => {
		const hotel = this.hotel();
		const type = this.type();

		if (!hotel || !type) return null;

		return scenarioMap[hotel]?.[type] ?? null;
	});

	numberOfResults = computed(() => {
		if (this.activeScenario() === 'vdc-hotels') {
			return (
				this.filterableListService.vdcHotels.value()?.hotels.length ?? 0
			);
		}
		if (this.activeScenario() === 'vdc-packages') {
			return (
				this.filterableListService.vdcPackages.value()?.packageBatches
					.length ?? 0
			);
		}
		if (this.activeScenario() === 'vdcvhi-hotels') {
			return (
				this.filterableListService.vdcvhiHotels.value()?.hotels
					.length ?? 0
			);
		}
		return 0;
	});

	filters = computed<FiltersInterface[]>(() => {
		const scenario = this.activeScenario();
		let rawFilters: FilterableListFilter[];

		if (scenario === 'vdc-hotels') {
			rawFilters = this.filterableListService.vdcHotels.value()?.filters;
		} else if (scenario === 'vdc-packages') {
			rawFilters =
				this.filterableListService.vdcPackages.value()?.filters;
		} else if (scenario === 'vdcvhi-hotels') {
			rawFilters =
				this.filterableListService.vdcvhiHotels.value()?.filters;
		}

		if (!rawFilters) return [];

		return rawFilters
			.filter((filter) => SUPPORTED_FILTER_TYPES.includes(filter.type))
			.map(({ title, type, data, maxValue }) => ({
				title,
				type,
				maxValue,
				items: (data ?? []).map(({ name, code, frequency, icon }) => ({
					name,
					value: code,
					frequency,
					icon,
				})),
			}));
	});

	private readonly hotelList = computed<FilterableHotel[]>(() => {
		const scenario = this.activeScenario();
		if (scenario === 'vdc-hotels') {
			return this.filterableListService.vdcHotels.value()?.hotels ?? [];
		}
		if (scenario === 'vdcvhi-hotels') {
			return (
				this.filterableListService.vdcvhiHotels.value()?.hotels ?? []
			);
		}
		return [];
	});

	private readonly packageBatchList = computed<FilterablePackageBatch[]>(
		() =>
			this.filterableListService.vdcPackages.value()?.packageBatches ??
			[],
	);

	sortOptions = computed(() => {
		// VHI hotels only support sorting by name (A-Z / Z-A)
		if (!this.showPrices()) {
			return DEFAULT_SORT_OPTIONS.filter(
				(option) => !option.value.startsWith('price'),
			);
		}

		return DEFAULT_SORT_OPTIONS;
	});

	shouldShowCarousel = computed(() => this.carousel().length > 0);

	carouselImages = computed<PhotoInterface[]>(() =>
		extractCarouselImages(this.carousel()),
	);

	carouselInputs = computed<(InputsType & { component: string })[]>(() =>
		this.carousel().map((blok) => ({
			...blok,
			component: blok['component'] ?? '',
		})),
	);

	contentInputs = computed<(InputsType & { component: string })[]>(() =>
		this.content().map((blok) => ({
			...blok,
			component: blok['component'] ?? '',
			isReadMore: true,
		})),
	);

	subContentInput = computed((): (InputsType & { component: string })[] => {
		const subContentValue = this.subContent();
		return subContentValue && subContentValue.length > 0
			? subContentValue.map((blok) => ({
					...blok,
					component: blok['component'] ?? '',
				}))
			: [];
	});

	carouselComponents: typeof carouselComponents = carouselComponents;
	titleComponents: typeof titleComponents = titleComponents;
	richTextComponents: typeof richTextComponents = richTextComponents;
	subContentComponents: typeof subContentComponents = subContentComponents;

	constructor() {
		super();

		effect(() => {
			negativeHeader$.next(this.shouldShowCarousel());
		});

		effect(() => {
			this.metadataService.updateMetaDataBatch(
				{
					meta: {
						title: this.metaTitle(),
						description: this.metaDescription(),
					},
					images: this.carouselImages(),
				},
				this.robotsNoFollow(),
				this.robotsNoIndex(),
			);
		});

		effect(() => {
			this.filterableListService.scenario.set(
				this.activeScenario() ?? undefined,
			);
			// Seed the filters once per scenario: plugin preselection
			// first, URL params win. Untracked so later URL changes
			// can't overwrite the visitor's selections.
			this.filterableListService.filters.set({
				...preselectedFilters(untracked(this.listItems)),
				...untracked(this.urlFilterParams),
			});
		});

		effect(() => {
			this.filterableListService.listItems.set(this.listItems());
		});

		effect(() => {
			if (!isPlatformServer(this.platformId)) return;
			const hotels = this.hotelList();
			if (!hotels.length) return;
			untracked(() => {
				this.metadataService.setSchemaOrgHotelList(
					this.metaTitle(),
					this.metaDescription(),
					hotels,
				);
			});
		});

		effect(() => {
			if (!isPlatformServer(this.platformId)) return;
			if (this.activeScenario() !== 'vdc-packages') return;
			const packageBatches = this.packageBatchList();
			if (!packageBatches.length) return;
			untracked(() => {
				this.metadataService.setSchemaOrgFilterablePackageList(
					this.metaTitle(),
					this.metaDescription(),
					this.carouselImages(),
					packageBatches,
				);
			});
		});
	}
}
